import React, { ReactElement, useEffect, useState } from 'react';
import CardActions from '@mui/material/CardActions';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import * as _ from 'lodash';
import QuestionBox from '../box';
import QuestionValue from '../value';
import MultiTypeFieldEditor from '../../field/editors/multitypefieldeditor';
import {
  BaseValueTypes,
  ColumnValue,
  QuestionEditorProps,
  QuestionSpec,
  SelectQuestionSpec,
  TableQuestionSpec,
  TableType
} from '../../../types/types';
import QuestionEditorButton from '../questioneditorbutton';
import QuestionLabel from '../label';
import { useStyles as tableHeadStyles } from '../../table/head';
import CustomButton, { CustomIconButton } from '../../button/button';
import ReactKeyUtils from '../../../utils/reactkeyutils';

const parseDate = (raw: string): string | undefined => {
  if (!raw) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) return `${slashMatch[3]}-${slashMatch[2].padStart(2, '0')}-${slashMatch[1].padStart(2, '0')}`;
  const dashMatch = raw.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dashMatch) return `${dashMatch[3]}-${dashMatch[2].padStart(2, '0')}-${dashMatch[1].padStart(2, '0')}`;
  const dotMatch = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (dotMatch) return `${dotMatch[3]}-${dotMatch[2].padStart(2, '0')}-${dotMatch[1].padStart(2, '0')}`;

  return undefined;
};

const parseClipboardValue = (raw: string, column: ColumnValue): BaseValueTypes => {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  switch (column.type) {
  case 'number': {
    const num = parseFloat(trimmed.replace(/,/g, '.'));
    
    return Number.isNaN(num) ? undefined : num;
  }
  case 'percent': {
    const pct = parseFloat(trimmed.replace('%', '').replace(/,/g, '.').trim());
    
    return Number.isNaN(pct) ? undefined : pct / 100;
  }
  case 'date':
    return parseDate(trimmed);
  case 'select': {
    const choices = (column as unknown as SelectQuestionSpec).choices ?? [];
    const match = choices.find(c =>
      c.label.toLowerCase() === trimmed.toLowerCase() ||
        c.value.toLowerCase() === trimmed.toLowerCase()
    );
    
    return match?.value ?? undefined;
  }
  case 'amount':
    return undefined;
  default:
    return trimmed;
  }
};

function TableQuestionEditor(props: QuestionEditorProps<TableQuestionSpec>): ReactElement {
  const tableQuestionSpec = props.spec as TableQuestionSpec;
  const [value, setValue] = useState(props.answer?.value as Array<BaseValueTypes> || []);
  const computeIndexes = (val: Array<BaseValueTypes>) => val.map(() => (tableQuestionSpec.columns.map(() => ReactKeyUtils.generateRandomKey())));
  const [indexes, setIndexes] = useState<Array<string[]>>(value ? computeIndexes(value) : []);
  const computeCheckArray = (val: Array<BaseValueTypes>) => val.map(row => (
    tableQuestionSpec.columns.map((column) => {
      if (column.required) {
        if (row) {
          const columnValue = row[column.id as keyof typeof row];
          if (columnValue === 0 || (columnValue !== undefined && columnValue !== '')) {
            return true;
          }
        }

        return false;
      }

      return true;
    })
  )
  );
  const [checkArray, setCheckArray] = useState<Array<boolean[]>>(computeCheckArray(value) || []);
  const [notSubmittable, setNotSubmittable] = useState(false);
  const theme = useTheme();
  const classesTableHead = tableHeadStyles(theme);

  const baseInfo: BaseValueTypes = {};

  tableQuestionSpec.columns?.forEach((column) => {
    baseInfo[column.id] = '';
  });

  useEffect(() => {
    setNotSubmittable(value.length < 1 || !checkArray.every(item => item.every(Boolean)));
  }, [checkArray]);

  const reset = (myValue: Array<BaseValueTypes>) => {
    if (myValue) {
      setValue(myValue);
    } else {
      setValue([]);
    }
    setCheckArray(computeCheckArray(myValue || []) || []);
    setIndexes(myValue ? computeIndexes(myValue) : []);
  };

  const addObject = () => {
    setCheckArray([...checkArray, tableQuestionSpec.columns.map(column => !column.required)]);
    setValue([...(value as Array<BaseValueTypes>), baseInfo]);
    setIndexes([...indexes, tableQuestionSpec.columns.map(() => ReactKeyUtils.generateRandomKey())]);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData('text/plain');
    if (!text.includes('\t')) return;
    e.preventDefault();
    const rows = text.trim().split('\n').map(row => row.split('\t'));
    const newRows: Array<BaseValueTypes> = rows.map((rowCells) => {
      const rowObj: Record<string, BaseValueTypes> = {};
      tableQuestionSpec.columns.forEach((column, i) => {
        rowObj[column.id] = parseClipboardValue(rowCells[i] ?? '', column);
      });
      
      return rowObj as BaseValueTypes;
    });
    setValue([...(value as Array<BaseValueTypes>), ...newRows]);
    setCheckArray([...checkArray, ...computeCheckArray(newRows)]);
    setIndexes([...indexes, ...newRows.map(() => tableQuestionSpec.columns.map(() => ReactKeyUtils.generateRandomKey()))]);
  };

  const deleteObject = (index: number) => {
    if ((value as Array<BaseValueTypes>).length > 1) {
      setCheckArray(checkArray.filter((key, i) => i !== index));
      setValue((value as Array<BaseValueTypes>).filter((rowValue, rowIndex) => rowIndex !== index));
      setIndexes(indexes.filter((x, i) => i !== index));
    } else {
      setCheckArray([]);
      setValue([]);
      setIndexes([]);
    }
  };

  return (
    <QuestionBox>
      <QuestionLabel spec={props.spec} />
      <QuestionValue>

        <Table style={{ width: 'fit-content' }} onPaste={handlePaste}>
          { value.length > 0 &&
            <TableHead>
              <TableRow>
                { tableQuestionSpec.columns?.map(column => (
                  <TableCell
                    key={column.id}
                    className={classesTableHead.tableHead}
                    align='left'
                  >
                    <Typography variant='h3'>
                      {column.label}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          }
          <TableBody>
            { value && (value as Array<BaseValueTypes>).map((rowValue, rowIndex) => (
              <TableRow key={rowIndex.toString()}>
                { rowValue && tableQuestionSpec.columns.map((column, i) => (
                  <TableCell key={column.id} style={{ border: 'none' }}>
                    <MultiTypeFieldEditor
                      customKey={indexes[rowIndex][i]}
                      type={column.type}
                      index={rowIndex}
                      specs={column as QuestionSpec}
                      value={rowValue[column.id as keyof typeof rowValue]}
                      lastValue={value[rowIndex - 1]?.[column.id as keyof typeof rowValue]}
                      setValue={(v) => {
                        const myArray = _.cloneDeep(value);
                        const myCheckArray = _.cloneDeep(checkArray);

                        myCheckArray[rowIndex][i] = column.required ? v === 0 || !!v : true;
                        myArray[rowIndex] = {
                          ...(value as Array<TableType>)[rowIndex],
                          [column.id]: v as BaseValueTypes
                        };

                        setCheckArray(myCheckArray);
                        setValue(myArray);
                      }}
                    />
                  </TableCell>
                ))}
                <TableCell key='delete' style={{ border: 'none' }}>
                  <CustomIconButton variant='text' color='secondary' onClick={() => deleteObject(rowIndex)}><DeleteForeverIcon/></CustomIconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell style={{ border: 'none' }}>
                <CustomButton variant='text' onClick={addObject} small style={{ whiteSpace: 'nowrap' }}>Add New Row</CustomButton>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>

      </QuestionValue>
      <CardActions>
        <QuestionEditorButton response={value} setResponse={myValue => reset(myValue as Array<BaseValueTypes>)} defaultValue={props.answer?.value as Array<BaseValueTypes> || []} buttonsSettings={{ ...props.buttonsSettings, notSubmittable }} />
      </CardActions>
    </QuestionBox>
  );
}

export default TableQuestionEditor;
